import type { MessageKey } from '@ctcells/i18n'

/** 경로 문자열의 유일한 출처. 화면에서 경로를 손으로 쓰지 않는다. */
export const PATHS = {
  login: '/login',

  // 통합 콘솔
  monitoring: '/monitoring',
  batches: '/batches',
  batchNew: '/batches/new',
  batchHistory: '/batch-history',
  alarms: '/alarms',
  samples: '/samples',
  sampleNew: '/samples/new',
  recipes: '/recipes',
  recipeNew: '/recipes/new',
  /**
   * ⚠ **옛 주소 — 리다이렉트로만 산다**(2026-10-11). 옛 랩웨어 카탈로그(`/labware-definitions`)를 서버 v0.9.4 가 걷어
   *   화면도 걷었다. 템플릿 목록(`templates`)으로 보낸다. 새 링크에 쓰지 마라.
   */
  labware: '/labware',
  labwareNew: '/labware/new',
  /**
   * 레시피 v2 템플릿 — 🔴 레시피 화면 아래다(eric 2026-10-06 「템플릿 탭을 만들어 달라고 했는데.. 템플릿 버튼을 만들어서.. 템플릿
   * 버튼을 클릭해야.. 템플릿 리스트 보이게 해줘」 — 2026-10-13 부터 작업 준비 탭). 🔴 2026-10-26 부터 «레시피» 템플릿 하나뿐이다
   * (eric 「응.. 다 빼줘」) — 한 건 `…/recipe/standard`. 옛 종류 주소(`labware` · `reagent` · `work` · `deck` · `…/new`)는 이 목록으로 보낸다.
   * ⚠ 레시피 한 건 경로(`/recipes/:recipeId`)보다 고정 조각이라 라우터가 먼저 잡는다.
   */
  templates: '/recipes/templates',
  /** 표준 레시피(새 형식)에서 새 레시피 — 레시피 목록 위 창(「+ 새 레시피」 메뉴) */
  recipeNewStandard: '/recipes/new/standard',
  modules: '/modules',
  logs: '/logs',
  admin: '/admin',
  /** 관리 「일반」 탭. 탭을 주소와 묶어서 새로고침·뒤로가기가 탭을 기억한다(2026-09-18) */
  adminGeneral: '/admin/general',
  /**
   * 관리 「웹훅」 탭 — 등록된 웹훅 + 알람별 전달(eric 2026-10-28 「설정 탭을 따로 하나 더 만들고, 지금 거는 웹훅이라고 탭 이름 수정해 줘」).
   * ⚠ 그 내용은 2026-10-28 오전까지 「설정」 탭(`adminGeneral`)에 있었다 — 옛 즐겨찾기는 빈 설정 탭으로 온다(그대로 둔다 · 팀장 결정).
   */
  adminWebhooks: '/admin/webhooks',
  /**
   * 관리 › 설정 「걸린 것」 절로 가는 주소(`GET /safety/stops` · 2026-10-11) — 설정 탭 그 절로 리다이렉트된다.
   *
   * 🔴 **작업 상세의 복구 대기 구역이 이 경로로 건다.** 문자열을 그 화면에 손으로 쓰지 마라 —
   *    핸드오프에서 선 배치를 푸는 길이 화면에 없어 사람이 반나절을 썼던 자리다(2026-09-21).
   */
  adminStops: '/admin/stops',
  /**
   * ⚠ **옛 주소 — 리다이렉트로만 산다.** 「자원 점유」(`GET /resource-locks`)는 서버 v0.9.4 에서 걷혔고
   *   그 자리는 「걸린 것」 절이다. 즐겨찾기·옛 링크가 끊기지 않게 같은 절로 보낸다. 새 링크에 쓰지 마라.
   */
  adminResourceLocks: '/admin/resource-locks',
  account: '/account',

  // 태블릿 kiosk — 콘솔 앱 안의 한 모드다(별도 배포 단위가 아니다)
  tablet: '/tablet',
} as const

/**
 * 새 작업 화면에 **레시피를 미리 골라** 넘기는 질의 키.
 *
 * 🔴 **라우터 state 가 아니라 주소에 싣는다.** 새로고침·뒤로가기·주소 공유에서 그대로 살아야
 *    하는 값이고, 고른 것이 무엇인지 주소만 봐도 드러난다. state 는 새로고침에 사라진다.
 * ⚠ 이 값은 **고르기 편의**일 뿐이다 — 새 작업 화면은 넘어온 id 가 고를 수 있는 목록
 *   (`committed`)에 실제로 있을 때만 「작업 만들기」를 연다. 없는 id 로 버튼이 서지 않는다.
 */
export const BATCH_NEW_RECIPE_PARAM = 'recipe'

/**
 * 알람 상세 서랍(`/alarms/:alarmId`). 🔴 목록 행 클릭 · 첫 칸 링크 · 라우트 표가 같은 출처를 쓴다
 * (eric 2026-09-28 「알람에서도 구조를 오른쪽으로.. 상세 페이지 보이게」).
 */
export const ALARM_DETAIL_ROUTE = `${PATHS.alarms}/:alarmId`

export function alarmDetailPath(alarmId: string): string {
  return `${PATHS.alarms}/${encodeURIComponent(alarmId)}`
}

/** 「이 레시피로 작업 만들기」가 가는 곳. 경로 문자열을 화면에서 짓지 않는다 */
export function batchNewPath(recipeId: string): string {
  return `${PATHS.batchNew}?${BATCH_NEW_RECIPE_PARAM}=${encodeURIComponent(recipeId)}`
}

/** RBAC 권한 5종. 집행은 서버 403 INSUFFICIENT_ROLE — 여기 값은 노출 판단용이다. */
export type Permission = 'view' | 'monitor' | 'control' | 'settings' | 'maintenance'

export interface NavItem {
  /**
   * 안정적인 항목 식별자. 경로가 바뀌어도 이것은 안 바뀐다 —
   * UI 패키지가 아이콘·배지를 이 값으로 물린다(코어가 아이콘을 알 필요는 없다).
   */
  id: NavItemId
  path: string
  /** 화면 표기는 사전이 쥔다 — 이 표에 한글을 넣지 않는다(docs/i18n.md) */
  labelKey: MessageKey
  /** 진입 권한. 없으면 숨기지 않고 비활성 + 사유 */
  permission: Permission
  /**
   * 이 문 «안»에 든 다른 경로들 — 탭이 제 경로를 가질 때(관리 › 모듈 `/modules` · 로그 `/logs`, 준비 › 레시피
   * `/recipes` · 랩웨어 `/labware`). 헤더는 `path` 와 이 목록 중 하나에 있으면 그 문을 «현재»로 표시한다
   * (eric 2026-09-22 「관리 › 모듈 탭이 선택되면 헤더의 관리 선택이 없어진다」).
   * ⚠ 이동 목적지는 여전히 `path` 하나다 — 이 목록은 «어디에 있는가» 판정에만 쓴다.
   */
  also?: readonly string[]
}

export type NavItemId =
  | 'monitoring'
  | 'batches'
  | 'batchHistory'
  | 'alarms'
  | 'resources'
  | 'samples'
  | 'recipes'
  | 'modules'
  | 'logs'
  | 'admin'
  | 'account'

export interface NavGroup {
  id: string
  labelKey: MessageKey
  items: NavItem[]
}

/**
 * 헤더 메뉴 — **모니터링 · 작업 관리 · 실험 준비 · 알람 · 관리** 다섯이다(`ConsoleHeader` 가 이 표를 편다).
 *    이름은 디자인팀 시안 2026-10-01 을 따른다 — 값은 `ko.ts` 의 `nav.item.*` 한 곳뿐이다.
 *
 * 🔴 **순서가 이 배열의 순서다.** `ConsoleHeader` 는 `flatMap` 으로 펼치기만 한다 —
 *    헤더에서 다시 정렬하지 않는다. 순서를 바꾸려면 여기를 바꾼다(시험 `shell.test.tsx`).
 * ⚠ **무리(`NavGroup`)의 `labelKey` 는 지금 아무 데도 안 그려진다.** 사이드바가 사라지면서
 *   무리는 «순서를 담는 칸»만 남았다 — 무리 이름을 근거로 항목 자리를 정하지 마라.
 * 설정(내 계정)은 여기 없다 — 계정 메뉴에서만 진입한다.
 * ⚠ 장비 관점 콘솔은 제 표(`apps/console-equipment/src/shell/equipmentNav.ts`)를 쓴다 — 이 표가 아니다.
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    id: 'ops',
    labelKey: 'nav.group.ops',
    items: [
      {
        id: 'monitoring',
        path: PATHS.monitoring,
        labelKey: 'nav.item.monitoring',
        permission: 'monitor',
      },
      /*
       * 🔴 **이력은 「작업」의 탭이다**(2026-09-18 eric 지시 — "작업 안에 이력 알람 다 포함시켜줘").
       *    항목을 되살리지 마라. 탭 목록의 유일한 출처는 `@ctcells/ui` 의 `OPS_TABS` 다.
       * ⚠ **모니터링은 합치지 않았다.**
       * 🔴 문의 권한이 `view` → `monitor` 로 내려갔다. `GET /batches` 를 서버가 전원에게 열어서다
       *    (`views/batches.py` `require_roles(ROLES)`) — `view` 로 두면 viewer 가 **서버가 허락한
       *    작업 목록**을 잃는다. 이력 탭은 제 `view` 를 따로 건다(`opsTabs.ts`).
       * 🔴 이력 탭(`/batch-history`)도 이 문의 경로다(`also`) — 제 경로라 접두어 판정에 안 걸려 헤더 불이 꺼졌다
       *    (2026-10-03). 상시 작업 줄의 메뉴 판정(`useStripFold`)도 이 표를 읽는다.
       */
      {
        id: 'batches',
        path: PATHS.batches,
        labelKey: 'nav.item.batches',
        permission: 'monitor',
        also: [PATHS.batchHistory],
      },
    ],
  },
  {
    id: 'res',
    labelKey: 'nav.group.res',
    items: [
      /*
       * 🔴 **시료·레시피·랩웨어는 문 하나로 묶였다**(2026-09-18 eric 지시 —
       *    "리소스 카테고리 자원 메뉴에 세개 넣으라고"). 항목을 되살리지 마라.
       *    탭 목록의 유일한 출처는 `@ctcells/ui` 의 `RESOURCE_TABS` 다.
       * ⚠ 그 문의 **메뉴 이름은 「실험 준비」**다(2026-09-21 eric 지시로 「자원」→「준비」 · 2026-10-01 시안 「실험 준비」).
       *    무리 제목 h2 는 「준비」(`screen.resourcesGroup.title`) 그대로다.
       *    id·경로·키 이름은 `resources` 그대로다 — 값은 `ko.ts` 의 `nav.item.resources` 한 곳뿐이다.
       * ⚠ **그룹 이름 「리소스」는 그대로 둔다.** 항목이 하나만 남는 모양인데 그게 지시다.
       *
       * 🔴 **문의 권한이 `view` → `monitor` 로 내려갔다**(2026-09-21 · 작업·관리와 같은 꼴이 됐다).
       *    세 탭이 더는 같은 권한이 아니기 때문이다 — 서버가 **시료만** operator/admin 으로 막고
       *    (`views/samples.py` · PII 접촉면) **레시피·랩웨어에는 viewer 에게도 200 을 준다**
       *    (`views/recipes.py`·`views/labware.py` 의 조회가 `require_roles(ROLES)`).
       *    문을 `view` 로 두면 viewer 가 **서버가 허락한 레시피·랩웨어까지** 잃는다.
       *    ⇒ 문은 가장 약한 권한으로 두고 **탭이 제 권한을 건다**(`resourceTabs.ts`).
       * ⚠ 이건 **노출 판단**일 뿐이다. 집행은 서버 403 `INSUFFICIENT_ROLE` 이다.
       */
      {
        id: 'resources',
        path: PATHS.samples,
        labelKey: 'nav.item.resources',
        permission: 'monitor',
        also: [PATHS.recipes],
      },
    ],
  },
  {
    /*
     * 🔴 **알람은 준비와 관리 사이다**(eric 2026-09-23 「헤더에 준비를 알람 왼쪽으로 순서를
     *    바꿔줘. 알람과 준비 위치를 바꿔줘」). 종전에는 「작업」 무리 안 셋째 항목이라
     *    작업 바로 오른쪽에 섰다 — 자리를 옮기려면 무리를 나오는 수밖에 없었다.
     * 🔴 **제 무리인 것이 실제와 맞다.** 알람은 2026-09-22 에 「작업」의 탭에서 나와
     *    **제 문**이 됐다(아래 항목 주석) — 그때 항목만 나오고 무리는 그대로였다.
     * ⚠ `labelKey` 는 항목 것을 그대로 쓴다. 무리 이름은 어디에도 안 그려지고
     *   (위 머리말) 이 무리에 쓸 사전 키를 새로 만들 까닭이 없다.
     */
    id: 'alarm',
    labelKey: 'nav.item.alarms',
    items: [
      /*
       * 🔴 **알람은 「작업」에서 «다시 나와» 제 문이 됐다**(2026-09-22 eric 지시 —
       *    「작업에서 알람탭을 따로 떼줘」). 2026-09-18~09-22 사이에만 「작업」의 탭이었다.
       *    ⇒ `OPS_TABS` 에 알람을 도로 넣지 마라. 되돌리면 `useAlarmBadge.test.tsx` 와
       *      `groupTabRule.test.tsx` 가 걸린다.
       * 🔴 **헤더의 활성 알람 배지가 붙는 자리이기도 하다**(`shell/useAlarmBadge.ts`).
       * 권한 `monitor` — `GET /alarms` 는 서버가 **세 역할 전부**에게 연다
       * (`app/views/alarms.py:131 ROLES` · 전수표는 `packages/ui/src/test/roleAccess.ts`).
       * viewer 는 `monitor` 를 쥐므로 이 문이 잠기지 않는다.
       */
      { id: 'alarms', path: PATHS.alarms, labelKey: 'nav.item.alarms', permission: 'monitor' },
    ],
  },
  {
    id: 'sys',
    labelKey: 'nav.group.sys',
    items: [
      /*
       * 🔴 **모듈·로그는 사이드바에서 빠지고 「관리」의 탭이 됐다**(2026-09-18 eric 지시
       *    — "모듈과 로그를 관리 메뉴에 다 넣어줘"). 항목을 되살리지 마라. 탭 목록의 유일한
       *    출처는 `@ctcells/ui` 의 `ADMIN_TABS` 다.
       *
       * 🔴 **그래서 이 문의 권한이 `settings` → `monitor` 로 내려갔다.** 모듈·로그가 `monitor`
       *    였기 때문이다 — 문을 `settings` 로 두면 `monitor` 만 가진 사용자가 **지금 보던 모듈·로그를
       *    못 보게 된다.** 대신 **탭마다 제 권한을 건다**(사용자·일반 = `settings`). 그래서 각 화면에
       *    실제로 닿는 사람은 이 변경 전후가 같다.
       * ⚠ 이건 **노출 판단**일 뿐이다. 집행은 서버 403 `INSUFFICIENT_ROLE` 이다.
       */
      /*
       * 🔴 **「관리」를 누르면 모듈 탭이 먼저 열린다**(eric 2026-10-02 「모듈 탭을 기본 탭으로 해줘. 지금 사용자 탭이
       *    디폴트인 거 같아」). 문의 주소가 `PATHS.modules` 다 — 사용자(`PATHS.admin`) · 로그는 `also` 로 같은 문에
       *    묶여 그 탭에 있어도 「관리」가 켜진다. `PATHS.admin` 자체(사용자 탭 주소)는 그대로다.
       */
      {
        id: 'admin',
        path: PATHS.modules,
        labelKey: 'nav.item.admin',
        permission: 'monitor',
        also: [PATHS.admin, PATHS.logs],
      },
    ],
  },
]

/**
 * 내 계정(설정). 사이드바 그룹에 넣지 않는다 — 계정 메뉴에서만 진입한다(README §2-2).
 */
export const ACCOUNT_NAV_ITEM: NavItem = {
  id: 'account',
  path: PATHS.account,
  labelKey: 'nav.item.account',
  permission: 'monitor',
}
